<template>
    <div class="popup-pls-reset">
        <p class="subtitle">{{ subtitle }}</p>

        <div class="content">
            <div class="product-list hide-scrollbar">
                <div class="product" v-for="product in items" :key="product.id" :class="{ selected: isEditing(product) }">
                    <div class="product-name">{{ product.name }}</div>
                    <div class="product-quantity">{{ product.quantity }}</div>
                    <button type="button" class="product-edit" @click="editProduct(product)">
                        <i class="i-andy-edit i-andy-s" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <div class="numpad-panel" v-if="editingProduct">
                <Numpad
                    :key="editingProduct.id"
                    @sendValue="changeValue"
                    @saveAndClose="saveQuantity"
                    :givenValue="editingValue"
                    :decimal="false"
                    :hasNegative="false"
                    :pinType="'numeric'"
                    :newDesing="true"
                    :quantity="true"
                    :max="maxQuantity"
                    :min="0"
                    :preventMobileKeyboard="true"
                ></Numpad>
            </div>
        </div>
    </div>
</template>

<script>
import Numpad from '@/components/inputs/Numpad'

/** Cantidad máxima que se puede introducir para un producto en retención. */
const MAX_QUANTITY = 999

export default {
    name: 'PopupPlsReset',
    components: {
        Numpad
    },
    props: {
        productsList: { type: Array, default: () => [] },
        subtitle: { type: String, default: '' }
    },
    data() {
        return {
            items: this.productsList.map((product) => ({ ...product })),
            editingProduct: null,
            editingValue: 0,
            maxQuantity: MAX_QUANTITY
        }
    },
    methods: {
        isEditing(product) {
            return this.editingProduct !== null && this.editingProduct.id === product.id
        },
        /** Pasa al listado la cifra que hay ahora en el teclado, sin cerrar la edición. */
        commitEditingQuantity() {
            if (!this.editingProduct) {
                return
            }

            const quantity = parseInt(this.editingValue, 10)
            this.editingProduct.quantity = isNaN(quantity) ? 0 : quantity
        },
        editProduct(product) {
            if (this.editingProduct && this.editingProduct.id !== product.id) {
                this.commitEditingQuantity()
            }

            this.editingProduct = product
            this.editingValue = product.quantity
            this.$emit('editing', true)
        },
        changeValue(value) {
            this.editingValue = value
        },
        saveQuantity() {
            this.commitEditingQuantity()
            this.editingProduct = null
            this.$emit('editing', false)
            this.$emit('change', this.getItems())
        },
        getItems() {
            this.commitEditingQuantity()
            if (this.editingProduct) {
                this.editingProduct = null
                this.$emit('editing', false)
            }

            return this.items.map((product) => ({ ...product }))
        }
    }
}
</script>

<style scoped lang="scss">
.popup-pls-reset {
    width: 100%;
    padding: 16px;
    @include display-flex();
    @include flex-direction(column);
    gap: 16px;

    .subtitle {
        font-family: $text;
        @include font-size(14px);
        color: $color-neutral-800;
        padding: 0;
        text-align: left;
    }

    .content {
        @include display-flex();
        @include flex-direction(row);
        @include align-items(flex-start);
        gap: 24px;
        width: 100%;
    }

    .numpad-panel {
        @include display-flex();
        @include justify-content(center);
        flex: 0 0 auto;

        // El numpad trae un margen propio que desalinea su primera fila con la lista.
        ::v-deep .num-padtop {
            margin-top: 0;
        }
    }

    .product-list {
        @include display-flex();
        @include flex-direction(column);
        gap: 8px;
        flex: 1;
        min-width: 0;
        max-height: 400px;
        overflow-y: auto;
    }

    .product {
        @include display-flex();
        @include align-items(center);
        gap: 8px;

        .product-name {
            flex: 1;
            min-width: 0;
            padding: 12px 16px;
            border: 1px solid $color-neutral-300;
            border-radius: 4px;
            font-family: $text-bold;
            @include font-size(16px);
            color: $color-neutral-800;
            text-align: left;
        }

        .product-quantity {
            width: 56px;
            padding: 12px 8px;
            border: 1px solid $color-secondary-300;
            border-radius: 4px;
            background-color: $color-secondary-100;
            font-family: $text-bold;
            @include font-size(16px);
            color: $color-secondary-500;
            text-align: center;
        }

        .product-edit {
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            width: 48px;
            height: 48px;
            padding: 0;
            border: 1px solid $color-neutral-300;
            border-radius: 4px;
            background-color: $color-white;
            cursor: pointer;

            i {
                color: $color-neutral-800;
            }
        }

        &.selected {
            .product-name,
            .product-edit {
                border-color: $color-secondary-300;
                background-color: $color-secondary-100;
                color: $color-secondary-500;
            }

            .product-edit i {
                color: $color-secondary-500;
            }
        }
    }
}
</style>
